import type { UploadMentorMediaParams } from "@/lib/api/mentor-profile";
import { isHeicFile } from "@/lib/utils/heic-converter";
import { processImage } from "@/lib/utils/image";

export const VERTICAL_IMAGE_ERROR = {
  notImage: "That file isn't an image. Please choose a JPG or PNG.",
  notVertical: "Please use a vertical photo (taller than it is wide).",
  uploadFailed: "We couldn't save that photo. Please try again.",
} as const;

export type VerticalImageErrorMessage =
  (typeof VERTICAL_IMAGE_ERROR)[keyof typeof VERTICAL_IMAGE_ERROR];

function readImageDimensions(
  file: Blob,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image."));
    };
    image.src = url;
  });
}

export async function uploadVerticalMentorImage(
  file: File,
  {
    filename,
    upload,
  }: {
    filename: string;
    upload: (params: UploadMentorMediaParams) => Promise<string>;
  },
): Promise<
  { ok: true; url: string } | { ok: false; error: VerticalImageErrorMessage }
> {
  let prepared = file;
  try {
    if (isHeicFile(file)) {
      prepared = await processImage(file);
    } else if (!file.type.startsWith("image/")) {
      return { ok: false, error: VERTICAL_IMAGE_ERROR.notImage };
    }

    const { width, height } = await readImageDimensions(prepared);
    if (height < width) {
      return { ok: false, error: VERTICAL_IMAGE_ERROR.notVertical };
    }

    const url = await upload({
      blob: prepared,
      filename: prepared.name || filename,
    });
    return { ok: true, url };
  } catch {
    return { ok: false, error: VERTICAL_IMAGE_ERROR.uploadFailed };
  }
}
